import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme, alpha, darken, lighten, useTheme } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';

// Tokens del módulo Datos (ver .claude/skills/scidatahub-design): estilo suizo, neutros y un acento.
export const radius = { chip: '6px', control: '8px', card: '12px', modal: '16px' };
// Movimiento: salida exponencial (responde de inmediato) y duraciones cortas; nada de `transition: all`.
export const motion = { ease: 'cubic-bezier(0.23, 1, 0.32, 1)', fast: '160ms', base: '200ms' };
const press = {
  transition: `background-color ${motion.fast} ease, border-color ${motion.fast} ease, color ${motion.fast} ease, transform ${motion.fast} ${motion.ease}`,
  '&:active:not(.Mui-disabled)': { transform: 'scale(0.97)' }, // se siente que el botón escuchó
  '@media (prefers-reduced-motion: reduce)': { '&:active:not(.Mui-disabled)': { transform: 'none' } },
};
const PLEX = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", sans-serif';
// Cifras siempre en IBM Plex Sans tabular: alinea columnas, ejes y KPI en cualquier tema (Titillium no tiene cifras tabulares).
export const NUM = { fontFamily: PLEX, fontVariantNumeric: 'tabular-nums' };
const SERIF = '"Source Serif 4", Georgia, "Times New Roman", serif';
// Titulares sueltos (tarjetas, gráficas): toman la fuente y el peso de titular del tema activo.
export const HEAD = { fontFamily: (t) => t.typography.h4.fontFamily, fontWeight: (t) => t.typography.h4.fontWeight };
// Barra de datos (frecuencias, completitud, puntajes) en el color de serie del tema, no en el acento: en rojo editorial
// una columna de barras rojas parecía una lista de alertas. Las barras de carga siguen en el acento.
export const dataBar = (h = 5) => ({
  height: h, borderRadius: '3px',
  bgcolor: (t) => alpha((t.datos?.chart || [t.palette.primary.main])[0], 0.14),
  '& .MuiLinearProgress-bar': { bgcolor: (t) => (t.datos?.chart || [t.palette.primary.main])[0] },
});

// Etiqueta en tinte suave del acento (un relleno sólido en rojo se lee como alerta); texto con contraste AA. En el rojo
// editorial el tinte es pizarra: varias etiquetas rosadas seguidas parecían avisos.
const softHue = (t) => (t.datos?.inkLinks ? t.palette.secondary.main : t.palette.primary.main);
export const SOFT = {
  bgcolor: (t) => alpha(softHue(t), t.palette.mode === 'dark' ? 0.2 : t.datos?.inkLinks ? 0.08 : 0.1),
  color: (t) => (t.datos?.inkLinks ? t.palette.text.primary : t.palette.mode === 'dark' ? t.palette.primary.light : t.palette.primary.dark),
  fontWeight: 600,
};
const TITILLIUM = '"Titillium Web", "IBM Plex Sans", system-ui, sans-serif';

// Temas institucionales: la administración del módulo elige uno (Administración → Resumen → Apariencia). El servidor
// guarda la elección y usa los mismos colores en las gráficas insertadas y en los informes (datos_publica.TEMAS).
export const PRESETS = {
  scidatahub: {
    label: 'SciDataHub',
    description: 'Esmeralda sobre papel cálido, titulares con serifa: la identidad de la plataforma.',
    heading: SERIF, body: PLEX, headingWeight: 600,
    light: {
      primary: { main: '#0b6e5a', light: '#1d9e75', dark: '#08503f', contrastText: '#ffffff' },
      secondary: { main: '#c2410c', contrastText: '#ffffff' },
      ink: '#141a17', muted: '#4b534f', bg: '#fbfbf9', paper: '#ffffff', line: 'rgba(20,26,23,0.12)',
      chart: ['#0e7c66', '#d55e00', '#0072b2', '#cc79a7', '#e69f00', '#56b4e9', '#3d3d3d', '#8a6d3b'],
      ramp: ['#e3f3ee', '#0b5e4d'], empty: '#eceeed', diverging: ['#c2410c', '#0e7c66'],
    },
    dark: {
      primary: { main: '#3ccfa6', light: '#7ee3c6', dark: '#08503f', contrastText: '#05201a' },
      secondary: { main: '#fb923c', contrastText: '#1a0d05' },
      ink: '#eef2f0', muted: '#a7b0ab', bg: '#0f1311', paper: '#161b19', line: 'rgba(255,255,255,0.12)',
      chart: ['#3ccfa6', '#ff9a52', '#56b4e9', '#e7a1c8', '#f0c04d', '#8fd3f7', '#c9cfcc', '#c9a36b'],
      ramp: ['#1c3a33', '#5eead4'], empty: '#2a2f2d', diverging: ['#fb923c', '#3ccfa6'],
    },
  },
  redalyc: {
    label: 'Rojo editorial',
    description: 'Acorde con la identidad de Redalyc: rojo sobre blanco, grises pizarra y titulares en Titillium Web.',
    heading: TITILLIUM, body: TITILLIUM, headingWeight: 700, inkLinks: true,
    light: {
      // #E3241B (rojo de Redalyc) cumple AA con texto blanco (4.6:1); el tono oscuro (5.8:1) para enlaces y hover
      primary: { main: '#e3241b', light: '#f05a52', dark: '#c81e16', contrastText: '#ffffff' },
      secondary: { main: '#212e33', contrastText: '#ffffff' },
      error: { main: '#9b1c1c' },
      ink: '#1c2327', muted: '#4d585c', bg: '#f4f6f7', paper: '#ffffff', line: 'rgba(33,46,51,0.14)',
      // una serie sola va en pizarra: el rojo en una línea o una nube de puntos se lee como alerta
      chart: ['#212e33', '#e3241b', '#0072b2', '#e69f00', '#56b4e9', '#009e73', '#76808a', '#cc79a7'],
      ramp: ['#fdecea', '#a51b13'], empty: '#eceff1', diverging: ['#0072b2', '#c81e16'],
    },
    dark: {
      primary: { main: '#ff6b61', light: '#ff8a80', dark: '#e3241b', contrastText: '#1c0f0e' },
      secondary: { main: '#b8c4ca', contrastText: '#121719' },
      error: { main: '#ff8a80' },
      ink: '#eef2f4', muted: '#a9b4b9', bg: '#121719', paper: '#1a2124', line: 'rgba(255,255,255,0.12)',
      chart: ['#b8c4ca', '#ff6b61', '#56b4e9', '#e69f00', '#8fd3f7', '#3cc6a0', '#98a3ac', '#e7a1c8'],
      ramp: ['#3a2321', '#ff8a80'], empty: '#2a3134', diverging: ['#56b4e9', '#ff6b61'],
    },
  },
};

const overrides = (mode, presetKey) => {
  const preset = PRESETS[presetKey] || PRESETS.scidatahub;
  const dark = mode === 'dark';
  const c = dark ? preset.dark : preset.light;
  const primary = c.primary.main;
  const head = { fontFamily: preset.heading, fontWeight: preset.headingWeight };
  return {
    palette: {
      primary: c.primary,
      // claro y oscuro calculados del secundario del tema: si no, se heredaban los del tema de la plataforma (naranja)
      secondary: { light: lighten(c.secondary.main, 0.35), dark: darken(c.secondary.main, 0.2), ...c.secondary },
      ...(c.error ? { error: c.error } : {}),
      text: { primary: c.ink, secondary: c.muted },
      divider: c.line,
      background: { default: c.bg, paper: c.paper },
    },
    // tokens propios del módulo: colores de gráficas y mapas del tema (useDatosTokens)
    datos: { preset: presetKey, chart: c.chart, ramp: c.ramp, empty: c.empty, diverging: c.diverging, inkLinks: Boolean(preset.inkLinks) },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: preset.body,
      h1: { ...head, letterSpacing: '-0.015em', lineHeight: 1.08 },
      h2: { ...head, letterSpacing: '-0.015em', lineHeight: 1.12 },
      h3: { ...head, letterSpacing: '-0.01em', lineHeight: 1.18 },
      h4: { ...head, letterSpacing: '-0.01em', lineHeight: 1.22 },
      h5: { fontFamily: preset.body, fontWeight: 650, letterSpacing: '-0.01em' },
      h6: { fontFamily: preset.body, fontWeight: 600, letterSpacing: '-0.005em' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      body1: { fontSize: preset.body === TITILLIUM ? '1rem' : '0.975rem', lineHeight: 1.6 },
      body2: { fontSize: preset.body === TITILLIUM ? '0.9rem' : '0.875rem', lineHeight: 1.55 },
      overline: { fontWeight: 600, letterSpacing: '0.04em', textTransform: 'none', fontSize: '0.78rem' },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    },
    components: {
      MuiButton: { styleOverrides: { root: { borderRadius: radius.control, padding: '8px 16px', ...press } } },
      MuiIconButton: { styleOverrides: { root: press } },
      MuiToggleButton: { styleOverrides: { root: { borderRadius: radius.control, textTransform: 'none', fontWeight: 500, ...press } } },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius.card, border: `1px solid ${c.line}`,
            transition: `border-color ${motion.fast} ease, box-shadow ${motion.base} ${motion.ease}`,
            // solo las tarjetas que se abren reaccionan al cursor: un marco rojo en una respuesta parecía una alerta
            '@media (hover: hover) and (pointer: fine)': {
              '&:has(.MuiCardActionArea-root):hover': {
                borderColor: alpha(dark ? c.ink : c.secondary.main, 0.35),
                boxShadow: dark ? 'none' : '0 1px 2px rgba(20,26,23,0.04), 0 4px 12px rgba(20,26,23,0.06)',
              },
            },
          },
        },
      },
      MuiPaper: { styleOverrides: { root: { borderRadius: radius.card }, outlined: { border: `1px solid ${c.line}` } } },
      MuiChip: { styleOverrides: { root: { borderRadius: radius.chip, fontWeight: 500 }, outlined: { borderColor: c.line }, clickable: press } },
      MuiTextField: { styleOverrides: { root: { '& .MuiOutlinedInput-root': { borderRadius: radius.control } } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: radius.control } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: radius.control } } },
      MuiAccordion: { styleOverrides: { root: { borderRadius: `${radius.control} !important` } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: radius.modal } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
      MuiTooltip: { styleOverrides: { tooltip: { borderRadius: radius.chip, fontSize: '0.78rem' } } },
      MuiLink: {
        styleOverrides: {
          root: preset.inkLinks
            ? { color: c.ink, textDecorationColor: alpha(primary, 0.55), transition: `color ${motion.fast} ease, text-decoration-color ${motion.fast} ease`,
              '&:hover': { color: dark ? c.primary.light : c.primary.dark, textDecorationColor: 'currentColor' } }
            : { color: dark ? c.primary.light : c.primary.dark },
        },
      },
      MuiTabs: { styleOverrides: { indicator: { transition: `left ${motion.base} ${motion.ease}, width ${motion.base} ${motion.ease}` } } },
      // tablas: cifras alineadas (Plex tabular) aunque el tema use Titillium
      MuiDataGrid: { styleOverrides: { root: { fontFamily: PLEX, fontVariantNumeric: 'tabular-nums' } } },
    },
  };
};

// ---------------------------------------------------------------- tema elegido por la administración
const STORE_KEY = 'datos-tema';
const readStored = () => {
  try { return window.localStorage.getItem(STORE_KEY); } catch { return null; }
};
// Estado compartido por todas las instancias del tema (la barra superior y las rutas del módulo usan una cada una):
// al elegir otro tema en el panel, todas cambian al instante.
let current = PRESETS[readStored()] ? readStored() : 'scidatahub';
let fetched = false;
const listeners = new Set();
const publish = (t) => {
  if (!PRESETS[t] || t === current) return;
  current = t;
  try { window.localStorage.setItem(STORE_KEY, t); } catch { /* sin almacenamiento: se usa el del servidor */ }
  listeners.forEach((fn) => fn(t));
};
const loadTema = () => {
  if (fetched) return;
  fetched = true; // una sola consulta por carga de página
  fetch('/api/datasets/apariencia', { credentials: 'include', cache: 'no-cache' })
    .then((r) => (r.ok ? r.json() : {})).then((d) => publish(d.tema)).catch(() => {});
};

const TemaContext = createContext({ tema: 'scidatahub', setTema: () => {} });
export const useDatosTema = () => useContext(TemaContext);
// Colores de gráficas y mapas del tema activo (con respaldo por si un componente se usa fuera del tema).
export const useDatosTokens = () => useTheme().datos || overrides('light', 'scidatahub').datos;

// Tema anidado del módulo Datos: hereda el modo claro/oscuro de la plataforma y aplica el tema institucional.
export function DatosTheme({ children }) {
  const [tema, setTemaState] = useState(current);
  useEffect(() => {
    listeners.add(setTemaState);
    loadTema();
    if (current !== tema) setTemaState(current);
    return () => { listeners.delete(setTemaState); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const ctx = useMemo(() => ({ tema, setTema: publish }), [tema]);
  const theme = useCallback((outer) => createTheme(outer, overrides(outer.palette.mode, tema)), [tema]);
  return (
    <TemaContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <GlobalStyles styles={browserSurfaces} />
        {children}
      </ThemeProvider>
    </TemaContext.Provider>
  );
}

// Lo que el navegador dibuja por su cuenta (selección, cursor, foco, subrayados) también lleva el tema.
const browserSurfaces = (t) => ({
  '::selection': { backgroundColor: alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.35 : 0.18), color: t.palette.text.primary },
  'input, textarea': { caretColor: t.palette.primary.main },
  'a, .MuiLink-root': { textUnderlineOffset: '0.2em', textDecorationThickness: '1px' },
  ':focus-visible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: '2px' },
  '@media (prefers-reduced-motion: reduce)': {
    '*, *::before, *::after': { animationDuration: '1ms !important', transitionDuration: '1ms !important', scrollBehavior: 'auto !important' },
  },
});

// Rutas del módulo envueltas en su tema.
export default function DatosLayout() {
  return <DatosTheme><Outlet /></DatosTheme>;
}
